import { useCallback, useEffect, useState } from 'react';
import { CircleAlert, ExternalLink, RefreshCw, Send } from 'lucide-react';
import githubInviteService from '../../../services/githubInviteService';
import { toast } from '../../../ui';
import { formatDate } from '../../../utils/format';

const GITHUB_NOTIFICATIONS = 'https://github.com/notifications';

// Statuses from Modules/Fulfillment/GitHubInvites.cs, worded for the customer.
const INVITE_META = new Map([
  ['Pending', { label: 'Waiting for you to accept', tone: 'warning' }],
  ['Accepted', { label: 'Access active', tone: 'success' }],
  ['Expired', { label: 'Invitation expired', tone: 'danger' }],
  ['Declined', { label: 'Invitation declined or removed', tone: 'danger' }],
  ['Failed', { label: "Invitation couldn't be sent", tone: 'danger' }],
  ['Removed', { label: 'Access ended' }],
  ['Unknown', { label: 'Checking…' }],
]);

const inviteMeta = status => INVITE_META.get(status) || { label: status };

const inviteHint = invite => {
  switch (invite.status) {
    case 'Pending':
      return invite.expiresAt
        ? `Accept it on GitHub by ${formatDate(invite.expiresAt)}, signed in as @${invite.gitHubUsername}.`
        : `Accept it on GitHub, signed in as @${invite.gitHubUsername}.`;
    case 'Expired':
      return 'GitHub invitations last 7 days. Get a new one below.';
    case 'Declined':
      return 'There is no open invitation on GitHub any more. Get a new one below.';
    case 'Failed':
      return invite.error || 'Check the GitHub username in your profile, then try again.';
    case 'Removed':
      return 'Your access period for this repository has ended.';
    default:
      return null;
  }
};

/**
 * GitHub repository access for a paid order: who gets it, the status of each repository invitation
 * (GET /api/github-invites/order/{orderNumber}) and a one-click resend that also works while an invitation is
 * still pending (the old one is cancelled and a fresh 7-day invitation is emailed by GitHub).
 */
const RepositoryAccess = ({ order, onAddUsername }) => {
  const username = order.fulfillment?.gitHubUsername || order.githubUsername || '';
  const [invites, setInvites] = useState(null); // null while loading the first time
  const [checking, setChecking] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [sending, setSending] = useState(null); // repository being re-sent, or '*' for all

  const load = useCallback(async () => {
    setChecking(true);
    const result = await githubInviteService.getInvites(order.orderId);
    setChecking(false);
    if (result.success) {
      setInvites(result.data);
      setLoadError('');
    } else {
      setInvites(current => current || []);
      setLoadError(result.error);
    }
  }, [order.orderId]);

  useEffect(() => {
    if (username) load();
  }, [load, username]);

  const resend = async repository => {
    setSending(repository || '*');
    const result = await githubInviteService.resend(order.orderId, repository);
    setSending(null);
    if (Array.isArray(result.data) && result.data.length) setInvites(result.data);
    if (result.success) toast.success(result.message || 'A new invitation is on its way');
    else toast.error({ content: result.error, duration: 6 });
  };

  const list = invites || [];
  const resendable = list.filter(invite => invite.canResend);

  return (
    <div className="pk-repo">
      <h3 className="pk-title-3">GitHub repository access</h3>

      {username ? (
        <p className="pk-repo__text">
          Access goes to{' '}
          <a
            className="pk-link"
            href={`https://github.com/${encodeURIComponent(username)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            @{username}
          </a>
          . GitHub sends that account an invitation for each repository. Accept it from your{' '}
          <a className="pk-link" href={GITHUB_NOTIFICATIONS} target="_blank" rel="noopener noreferrer">
            GitHub notifications
            <ExternalLink className="pk-repo__ext" aria-hidden="true" />
            <span className="pk-account__sr"> (opens in a new tab)</span>
          </a>{' '}
          or the email GitHub sends you. Invitations expire after 7 days; you can get a new one here any time.
        </p>
      ) : (
        <p className="pk-repo__text">Add your GitHub username so we can give you access to the repository.</p>
      )}

      {username && (
        <div className="pk-repo__live" aria-live="polite">
          {invites === null && <p className="pk-hint">Checking your invitations…</p>}

          {loadError && (
            <div className="pk-alert" data-tone="warning">
              <CircleAlert aria-hidden="true" />
              <span>{loadError}</span>
            </div>
          )}

          {list.length > 0 && (
            <ul className="pk-repo__results">
              {list.map(invite => {
                const meta = inviteMeta(invite.status);
                const hint = inviteHint(invite);
                const waiting = invite.nextResendAt && new Date(invite.nextResendAt) > new Date();
                return (
                  <li key={invite.repository} className="pk-repo__invite">
                    <div className="pk-repo__name">
                      <strong>{invite.productName || invite.repository}</strong>
                      <span>{invite.repository}</span>
                      {hint && <span>{hint}</span>}
                    </div>
                    <div className="pk-repo__side">
                      <span className="pk-badge" data-tone={meta.tone}>
                        {meta.label}
                      </span>
                      <div className="pk-repo__row-actions">
                        {invite.status === 'Pending' && (
                          <a
                            className="pk-btn pk-btn--primary pk-btn--xs"
                            href={invite.acceptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Accept on GitHub
                            <ExternalLink className="pk-repo__ext" aria-hidden="true" />
                          </a>
                        )}
                        {invite.status === 'Accepted' && (
                          <a
                            className="pk-btn pk-btn--secondary pk-btn--xs"
                            href={invite.repositoryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open repository
                            <ExternalLink className="pk-repo__ext" aria-hidden="true" />
                          </a>
                        )}
                        {invite.canResend && (
                          <button
                            type="button"
                            className={`pk-btn pk-btn--xs ${invite.status === 'Pending' ? 'pk-btn--ghost' : 'pk-btn--primary'}`}
                            onClick={() => resend(invite.repository)}
                            disabled={!!sending || waiting}
                            aria-busy={sending === invite.repository || undefined}
                            title={waiting ? 'A new invitation was just sent. You can request another in a few minutes.' : undefined}
                          >
                            <Send aria-hidden="true" />
                            {invite.status === 'Pending' ? 'Send a new invitation' : 'Resend invitation'}
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {invites !== null && list.length === 0 && !loadError && (
            <p className="pk-hint">Your invitation will show here once the repository access is set up.</p>
          )}
        </div>
      )}

      <div className="pk-repo__actions">
        {username && (
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm"
            onClick={load}
            disabled={checking}
            aria-busy={checking || undefined}
          >
            <RefreshCw aria-hidden="true" /> Refresh status
          </button>
        )}
        {resendable.length > 1 && (
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm"
            onClick={() => resend(null)}
            disabled={!!sending}
            aria-busy={sending === '*' || undefined}
          >
            <Send aria-hidden="true" /> Resend all invitations
          </button>
        )}
        {onAddUsername && (
          <button type="button" className="pk-btn pk-btn--ghost pk-btn--sm" onClick={onAddUsername}>
            {username ? 'Change GitHub username' : 'Add GitHub username'}
          </button>
        )}
      </div>
    </div>
  );
};

export default RepositoryAccess;
